import React, { useCallback, useMemo } from 'react';

import { useFormContext } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';
import { BodyBlockV2Item, StoreBodyBlock } from 'constants/enums';
import { useTemplateStudioState } from 'state/hooks/template-studio.hook';
import { TemplateStudioBlock } from 'state/template-studio.state.service';
import { z } from 'zod';

import Button from 'components/Button/Button';
import { revealBodyBlockInV2Order } from 'components/TemplatesStudio/bodyBlocksOrderV2.utils';
import { ETemplateStudioLayoutOptions } from 'components/TemplatesStudio/enums';
import { PopoverContent } from 'components/ui';

import { mapBlockToSecondarySidebar } from '../../../utils/blockForms.utils';
import { templateStudioSchema } from '../../../validation-schema';

import {
  resolveTemplateStudioAddBlockSections,
  TemplateStudioAddBlockSection,
  TemplateStudioAddBlockSectionRow
} from './addBlockModal.sections';

type FormValues = z.infer<typeof templateStudioSchema>;

const PLACEHOLDER_TILES = (
  <div className="box-border h-10 w-10 shrink-0 overflow-hidden rounded bg-violet-200 p-2">
    <div className="grid h-full w-full grid-cols-2 grid-rows-2 gap-0.5">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          role="presentation"
          className="block min-h-0 min-w-0 rounded-[2px] bg-white shadow-sm"
        />
      ))}
    </div>
  </div>
);

function AddBlockRow({
  row,
  dataTestId
}: {
  row: TemplateStudioAddBlockSectionRow;
  dataTestId: string;
}) {
  return (
    <div className="flex items-center gap-3 px-3 pb-2.5 pt-[14px] transition-colors hover:rounded-md hover:bg-base-primaryForeground">
      {PLACEHOLDER_TILES}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-sans not-italic text-p-s font-medium text-base-primary">
            {row.title}
          </span>
          {row.badgeLabel && (
            <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-indigo-100 text-indigo-700">
              {row.badgeLabel}
            </span>
          )}
        </div>
        <p className="mt-0.5 font-sans not-italic text-p-xs text-base-mutedForeground">
          {row.description}
        </p>
      </div>
      <Button
        type="button"
        variant="default"
        size="icon"
        className="shrink-0 rounded-md border-0 bg-indigo-600 text-white shadow-sm hover:bg-indigo-700 [&_i.font-awesome-icon]:!text-sm"
        aria-label={`Add ${row.title}`}
        onClick={row.onAdd}
        dataTestId={`${dataTestId}-add`}
      >
        <FontAwesomeIcon icon="fa-regular fa-plus" />
      </Button>
    </div>
  );
}

export type TemplateStudioAddBlockModalProps = {
  onClose: () => void;
  enabledBlocks: StoreBodyBlock[];
  sections?: TemplateStudioAddBlockSection[];
};

export const TemplateStudioAddBlockModal: React.FC<
  TemplateStudioAddBlockModalProps
> = ({ onClose, enabledBlocks, sections: sectionsProp }) => {
  const { getValues, setValue } = useFormContext<FormValues>();
  const {
    resetSidebars,
    setOpenSidebarBlock,
    setOpenSecondarySidebarBlock,
    setScrollToSection
  } = useTemplateStudioState();

  const scrollIfStoreBody = useCallback(
    (block: TemplateStudioBlock) => {
      if (
        (Object.values(StoreBodyBlock) as string[]).includes(block as string)
      ) {
        setScrollToSection(block as StoreBodyBlock);
      }
    },
    [setScrollToSection]
  );

  const focusBlockInStudio = useCallback(
    (block: TemplateStudioBlock) => {
      resetSidebars();
      setOpenSidebarBlock(block);
      setOpenSecondarySidebarBlock(
        mapBlockToSecondarySidebar[block] ? block : undefined
      );
      scrollIfStoreBody(block);
    },
    [
      resetSidebars,
      scrollIfStoreBody,
      setOpenSecondarySidebarBlock,
      setOpenSidebarBlock
    ]
  );

  const persistV2 = useCallback(
    (next: BodyBlockV2Item[]) => {
      setValue('bodyBlocksOrderV2', next, {
        shouldValidate: true,
        shouldDirty: true
      });
    },
    [setValue]
  );

  const handlePickRolling = useCallback(
    (layout: ETemplateStudioLayoutOptions) => {
      const current = getValues('bodyBlocksOrderV2') ?? [];
      const { nextItems, wasAlreadyVisible } = revealBodyBlockInV2Order(
        current,
        StoreBodyBlock.rollingOffers
      );
      persistV2(nextItems);
      if (!wasAlreadyVisible) {
        setValue('rollingOffers.desktopLayout', layout, {
          shouldValidate: true,
          shouldDirty: true
        });
      }
      onClose();
      focusBlockInStudio(StoreBodyBlock.rollingOffers);
    },
    [focusBlockInStudio, getValues, onClose, persistV2, setValue]
  );

  const handlePickBlock = useCallback(
    (block: StoreBodyBlock) => {
      const current = getValues('bodyBlocksOrderV2') ?? [];
      persistV2(revealBodyBlockInV2Order(current, block).nextItems);
      onClose();
      focusBlockInStudio(block);
    },
    [focusBlockInStudio, getValues, onClose, persistV2]
  );

  const sections = useMemo(
    () =>
      sectionsProp ??
      resolveTemplateStudioAddBlockSections({
        enabledBlocks,
        revealBlock: handlePickBlock,
        revealRollingWithLayout: handlePickRolling
      }),
    [sectionsProp, enabledBlocks, handlePickBlock, handlePickRolling]
  );

  return (
    <PopoverContent
      side="right"
      align="start"
      sideOffset={8}
      onCloseAutoFocus={(e) => e.preventDefault()}
      data-testid="TemplatesStudio-AddBlockModal"
      className="w-[425px] gap-0 overflow-hidden rounded-lg border bg-white p-0 shadow-xl"
    >
      <div className="space-y-0 border-b border-neutral-100 py-4 pl-6 pr-4 text-left">
        <p className="text-p-l font-semibold text-foreground">Add Block</p>
      </div>

      <div className="max-h-[min(70vh,520px)] space-y-4 overflow-y-auto px-2 py-6">
        {sections.length === 0 ? (
          <p className="text-sm text-base-mutedForeground">
            No blocks are available to add yet.
          </p>
        ) : (
          sections.map((section) => (
            <section key={section.id} className="space-y-2">
              <p className="pl-4 text-p-xs font-semibold uppercase tracking-wide !text-[color:var(--base-muted-foreground)]">
                {section.label}
              </p>
              <div className="space-y-2">
                {section.rows.map((row) => (
                  <AddBlockRow
                    key={row.id}
                    row={row}
                    dataTestId={`TemplatesStudio-AddBlockModal-${row.id}`}
                  />
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </PopoverContent>
  );
};
